import React, { useState } from 'react';
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import {
  Check as CheckIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import {
  ACTIVE_COLORS,
  NEXT_COLORS,
  COMPLETE_COLORS,
  PENDING_COLORS,
  BADGE_TRANSITION,
  BADGE_FONT_SIZE_ACTIVE,
  BADGE_FONT_SIZE_DEFAULT,
  compactBadgeBaseSx,
  expandedLabelSx,
  makeBorder,
} from './constants';
import type { BadgeNodeProps, BadgeState } from './types';

function getTooltipTitle(
  badgeState: BadgeState,
  isIssued: boolean,
  t: (key: string) => string,
): string {
  if (badgeState === 'active') return t('draftManuals.draftState.tooltipCurrent');
  if (badgeState === 'complete') return t('draftManuals.draftState.tooltipPrevious');
  if (badgeState === 'next') {
    return isIssued
      ? t('draftManuals.draftState.tooltipPublished')
      : t('draftManuals.draftState.tooltipNext');
  }
  return t('draftManuals.draftState.tooltipSequential'); // pending
}

export { getTooltipTitle };

const BadgeNode: React.FC<BadgeNodeProps> = ({
  status,
  badgeState,
  label,
  onClick,
  dimmed = false,
  disabled = false,
  showSpinner = false,
  tooltipTitle,
}) => {
  const shouldDim = dimmed && badgeState !== 'active';
  const [hovered, setHovered] = useState(false);

  const handleMouseEnter = () => {
    if (!dimmed) setHovered(true);
  };
  const handleMouseLeave = () => setHovered(false);

  const expanded = hovered || showSpinner;
  const dimmedSx = shouldDim ? { opacity: 0.5, pointerEvents: 'none' as const } : {};

  if (badgeState === 'complete') {
    const colors = COMPLETE_COLORS[status];
    return (
      <Tooltip title={disabled ? '' : tooltipTitle} arrow>
        <Box
          component='button'
          type='button'
          onClick={onClick}
          aria-label={label}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          sx={{
            ...compactBadgeBaseSx,
            gap: expanded ? 0.75 : 0,
            width: expanded ? 'auto' : 24,
            px: expanded ? 3 : 0,
            backgroundColor: colors.minimized.bg,
            border: makeBorder(colors.minimized),
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: colors.open.bg,
              border: makeBorder(colors.open),
            },
            ...dimmedSx,
          }}
        >
          {!expanded && (
            showSpinner
              ? <CircularProgress size={12} sx={{ color: colors.minimized.text, flexShrink: 0 }} />
              : <CheckIcon sx={{ fontSize: 14, color: colors.minimized.text, flexShrink: 0 }} />
          )}
          {expanded && (
            <>
              <Typography sx={{ ...expandedLabelSx, color: colors.minimized.text }}>
                {label}
              </Typography>
              {showSpinner
                ? <CircularProgress size={12} sx={{ color: colors.minimized.text, flexShrink: 0 }} />
                : <ArrowBackIcon sx={{ fontSize: 14, color: colors.minimized.text, flexShrink: 0 }} />
              }
            </>
          )}
        </Box>
      </Tooltip>
    );
  }

  if (badgeState === 'pending') {
    const pendingColors = PENDING_COLORS[status];
    return (
      <Tooltip title={disabled ? '' : tooltipTitle} arrow>
        <Box
          component='button'
          type='button'
          tabIndex={-1}
          onClick={onClick}
          aria-label={label}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          sx={{
            ...compactBadgeBaseSx,
            gap: expanded ? 0.75 : 0,
            width: expanded ? 'auto' : 24,
            px: expanded ? 3 : 0,
            backgroundColor: showSpinner ? 'transparent' : pendingColors.minimized.bg,
            cursor: 'default',
            border: makeBorder(pendingColors.minimized),
            '&:hover': {
              backgroundColor: pendingColors.open.bg,
              border: makeBorder(pendingColors.open),
            },
            ...dimmedSx,
          }}
        >
          {!expanded && showSpinner && (
            <CircularProgress size={12} sx={{ color: pendingColors.minimized.text, flexShrink: 0 }} />
          )}
          {expanded && (
            <>
              <Typography sx={{ ...expandedLabelSx, color: pendingColors.minimized.text }}>
                {label}
              </Typography>
              {showSpinner && (
                <CircularProgress size={12} sx={{ color: pendingColors.minimized.text, flexShrink: 0 }} />
              )}
            </>
          )}
        </Box>
      </Tooltip>
    );
  }

  // 'active' or 'next'
  const isActive = badgeState === 'active';
  const isNext = badgeState === 'next';

  const activeColors = ACTIVE_COLORS[status];
  const nextColors = NEXT_COLORS[status];

  const badgeBg = isNext ? nextColors.bg : activeColors.bg;
  const badgeBorder = isNext
    ? makeBorder(nextColors)
    : makeBorder(activeColors);
  const badgeText = isNext ? nextColors.text : activeColors.text;
  const hoverSx = isActive
    ? {}
    : { backgroundColor: nextColors.hover.bg, border: makeBorder(nextColors.hover) };

  return (
    <Tooltip title={disabled ? '' : tooltipTitle} arrow>
      <Box
        component='button'
        type='button'
        onClick={onClick}
        aria-label={label}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          verticalAlign: 'middle',
          boxSizing: 'border-box',
          gap: 0.5,
          height: isNext ? 24 : 32,
          borderRadius: '16px',
          backgroundColor: badgeBg,
          border: badgeBorder,
          cursor: isActive ? 'default' : 'pointer',
          px: 3,
          py: 0,
          flexShrink: 0,
          transition: `${BADGE_TRANSITION}, box-shadow 200ms ease-in-out`,
          boxShadow: isActive ? '0px 4px 8px -2px rgba(4, 41, 77, 0.10), 0px 2px 4px -2px rgba(4, 41, 77, 0.06)' : 'none',
          '&:hover': hoverSx,
          ...dimmedSx,
        }}
      >
        {isNext && <ArrowForwardIcon sx={{ fontSize: 12, color: badgeText, flexShrink: 0 }} />}
        <Typography
          variant='captionMd'
          sx={{
            color: badgeText,
            fontWeight: 500,
            whiteSpace: 'nowrap',
            lineHeight: 1,
            fontSize: isActive ? BADGE_FONT_SIZE_ACTIVE : BADGE_FONT_SIZE_DEFAULT,
          }}
        >
          {label}
        </Typography>
        {showSpinner && (
          <CircularProgress size={12} sx={{ color: badgeText, flexShrink: 0, ml: 0.5 }} />
        )}
      </Box>
    </Tooltip>
  );
};

export default BadgeNode;
